import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import { AiFillEye, AiOutlineFileAdd } from 'react-icons/ai'
import Pagination from '../Pagination'
import AssignAssessment from './AssignAssessment'
import ViewAssessments from './ViewAssessments'
import {
    displayEmployeeCode,
    progressLabel,
    progressClass,
    countByStatus,
    badgeBase
} from './trainingHelpers.js'

const STATUSES = ['assigned', 'in_progress', 'completed']

function TrainingTab({ searchTerm, sortType, filterData, page, setPage }) {
    const { t } = useTranslation()
    const [viewingTraining, setViewingTraining] = useState(null)
    const [assignEmployee, setAssignEmployee] = useState(null)
    const [employees, setEmployees] = useState([])
    const [employeeLoading, setEmployeeLoading] = useState(false)
    const [pagination, setPagination] = useState({
        page: 1,
        pageCount: 1,
        pageRangeDisplayed: 10,
        totalItems: 0
    })
    const [assignmentsByEmployee, setAssignmentsByEmployee] = useState({})

    const loadEmployees = () => {
        setEmployeeLoading(true)
        const payload = {
            page,
            pageSize: 10,
            keyword: searchTerm.trim(),
            startDate: filterData.startDate ? dayjs(filterData.startDate).format('YYYY-MM-DD') : null,
            endDate: filterData.endDate ? dayjs(filterData.endDate).format('YYYY-MM-DD') : null
        }
        if (sortType) {
            payload.sortBy = 'Employee.name'
            payload.sortType = sortType
        }
        apiGet(apiPath.trainingAssignmentsALLEmployees, payload)
            .then((result) => {
                const response = result?.data?.results
                setEmployees(result?.data?.success ? response?.docs || [] : [])
                setPagination({
                    page: response?.page || 1,
                    pageCount: response?.totalPages || 1,
                    pageRangeDisplayed: 10,
                    totalItems: response?.totalDocs || 0,
                    perPageItem: response?.docs?.length || 0
                })
            })
            .catch(() => {
                setEmployees([])
                setPagination((current) => ({ ...current, totalItems: 0, pageCount: 1 }))
            })
            .finally(() => setEmployeeLoading(false))
    }

    const loadAssignmentStatuses = () => {
        const pageSize = 100
        const loadPage = (page, collected) => apiGet(apiPath.trainingAssignments, { page, pageSize })
            .then((result) => {
                const payload = result?.data?.success ? result.data.results || {} : {}
                const docs = payload.docs || []
                const merged = collected.concat(docs)
                const total = Number(payload.totalDocs) || merged.length
                if (merged.length < total && docs.length && page < 20) return loadPage(page + 1, merged)
                return merged
            })
        loadPage(1, [])
            .then((docs) => {
                const grouped = {}
                docs.forEach((row) => {
                    const id = String(row.employeeID || '')
                    if (!id) return
                    if (!grouped[id]) grouped[id] = []
                    grouped[id].push(row)
                })
                setAssignmentsByEmployee(grouped)
                setViewingTraining((current) => {
                    if (!current?.employeeId) return current
                    return { ...current, assignments: grouped[String(current.employeeId)] || [] }
                })
            })
            .catch(() => setAssignmentsByEmployee({}))
    }

    // initial load of assignment statuses
    useEffect(() => {
        loadAssignmentStatuses()
    }, [])

    // debounced reload when filters / page change
    useEffect(() => {
        const timer = setTimeout(() => {
            loadEmployees()
            loadAssignmentStatuses()
        }, 400)
        return () => clearTimeout(timer)
    }, [page, searchTerm, sortType, filterData.startDate, filterData.endDate])

    // an assessment was removed from the View modal: update instantly, then re-sync
    const handleAssignmentRemoved = (id) => {
        const strip = (rows = []) => rows.filter((row) => row._id !== id)
        setViewingTraining((current) => (current ? { ...current, assignments: strip(current.assignments) } : current))
        setAssignmentsByEmployee((current) => Object.fromEntries(
            Object.entries(current).map(([key, rows]) => [key, strip(rows)])
        ))
        loadAssignmentStatuses()
    }

    const buildEmployeeInfo = (item) => {
        const person = item?.Employee || {}
        return {
            employeeId: person._id || item.employeeID,
            code: displayEmployeeCode(item),
            name: person.name || '',
            email: person.email || '',
            department: item?.Department?.name || '',
            job: item?.Job?.name || '',
            hireDate: item?.acceptedRejectedAt || ''
        }
    }

    return (
        <>
            <div className='p-3'>
                <div className='overflow-x-auto rounded-lg border relative'>
                    <table className='w-full text-xs text-left text-[#6c6c6c]'>
                        <thead className='text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE]'>
                            <tr>
                                <th className='py-3 px-4 whitespace-nowrap'>{t('SR_NO')}</th>
                                <th className='py-3 px-4 whitespace-nowrap'>{t('EMPLOYEE_ID')}</th>
                                <th className='py-3 px-4 whitespace-nowrap'>{t('EMPLOYEE_NAME')}</th>
                                <th className='py-3 px-4 whitespace-nowrap'>{t('JOB_TITLE')}</th>
                                <th className='py-3 px-4 whitespace-nowrap'>Assessments</th>
                                <th className='py-3 px-4 text-center whitespace-nowrap'>{t('O_ACTION')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.length ? employees.map((item, index) => {
                                const info = buildEmployeeInfo(item)
                                const assignments = assignmentsByEmployee[String(info.employeeId)] || []
                                const counts = countByStatus(assignments)
                                return (
                                    <tr key={item._id || info.employeeId} className='bg-white border-b hover:bg-gray-50'>
                                        <th scope='row' className='py-4 px-4 border-r font-medium text-gray-900'>
                                            {index + 1 + 10 * (page - 1)}
                                        </th>
                                        <td className='border-r py-4 px-4 text-gray-900'>{info.code || '—'}</td>
                                        <td className='text-gray-900 py-4 px-4 border-r'>{info.name || '—'}</td>
                                        <td className='py-4 px-4 border-r text-gray-900'>{info.job || '—'}</td>
                                        <td className='py-4 px-4 border-r'>
                                            {assignments.length ? (
                                                <div className='flex flex-wrap items-center gap-1.5'>
                                                    {STATUSES.filter((key) => counts[key] > 0).map((key) => (
                                                        <span key={key} className={`${badgeBase} ${progressClass(key)}`}>
                                                            {counts[key]} {progressLabel(key)}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className='text-gray-400'>None assigned</span>
                                            )}
                                        </td>
                                        <td className='py-4 px-4 text-center'>
                                            <div className='flex items-center justify-center gap-2'>
                                                <button
                                                    type='button'
                                                    className='w-8 h-8 inline-flex items-center justify-center rounded border border-gray-300 text-[#2f5bff] hover:bg-blue-50'
                                                    title='View assessments'
                                                    aria-label='View assessments'
                                                    onClick={() => setViewingTraining({ ...info, assignments })}
                                                >
                                                    <AiFillEye className='w-5 h-5' />
                                                </button>
                                                <button
                                                    type='button'
                                                    className='w-8 h-8 inline-flex items-center justify-center rounded bg-[#2f5bff] text-white hover:bg-blue-700 disabled:opacity-50'
                                                    title='Assign assessment'
                                                    aria-label='Assign assessment'
                                                    disabled={!info.employeeId}
                                                    onClick={() => setAssignEmployee(info)}
                                                >
                                                    <AiOutlineFileAdd className='w-5 h-5' />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            }) : (
                                <tr>
                                    <td className='py-6 px-4 text-center text-gray-500' colSpan={6}>
                                        {employeeLoading ? 'Loading…' : 'No matching employees.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {pagination?.totalItems ? (
                    <Pagination
                        page={page}
                        handlePageClick={(event) => setPage(event.selected + 1)}
                        options={pagination}
                    />
                ) : null}
            </div>

            {assignEmployee && (
                <AssignAssessment
                    employee={assignEmployee}
                    onClose={() => setAssignEmployee(null)}
                    onChange={loadAssignmentStatuses}
                />
            )}

            {viewingTraining && (
                <ViewAssessments
                    employee={viewingTraining}
                    onClose={() => setViewingTraining(null)}
                    onRemoved={handleAssignmentRemoved}
                    onAssign={() => {
                        const { assignments, ...info } = viewingTraining
                        setViewingTraining(null)
                        setAssignEmployee(info)
                    }}
                />
            )}
        </>
    )
}

export default TrainingTab